/**
 * ピンの見た目（アバター / 鮮度 / パルス / 自分ピン強調）。
 *
 * design §16 / 要件 7-1, 7-3, 7-4 に対応。
 * MapLibre の Marker に載せる HTML 要素を組み立てる。
 *
 * 種別バッジ（🔴/🔵/📍）は廃止。アバター表示で識別できるため不要（UX 判断）。
 */
import type { UserLocation } from '../api/client';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';

/** 鮮度の段階。design §16-2。 */
export type Freshness = 'pulse' | 'normal' | 'faded' | 'stale';

const TWO_MIN = 2 * 60;
const TEN_MIN = 10 * 60;
const THIRTY_MIN = 30 * 60;

/** 経過秒から鮮度段階を決める。 */
export function freshnessOf(ageSec: number): Freshness {
  if (ageSec <= TWO_MIN) return 'pulse';
  if (ageSec <= TEN_MIN) return 'normal';
  if (ageSec <= THIRTY_MIN) return 'faded';
  return 'stale';
}

/** 鮮度段階に応じた不透明度。古さは主に透明度で表現し「消えかけ」の印象にする。 */
function opacityOf(freshness: Freshness): number {
  switch (freshness) {
    case 'pulse':
    case 'normal':
      return 1;
    case 'faded':
      return 0.4;
    case 'stale':
      return 0.15;
  }
}

/** 表示名からイニシャル（先頭1文字）を得る。 */
function initialOf(name: string): string {
  const trimmed = name.trim();
  return trimmed ? Array.from(trimmed)[0].toUpperCase() : '?';
}

/** 経過分（切り捨て）。stale バッジの「N分前」に使う。 */
function minutesAgo(ageSec: number): number {
  return Math.floor(ageSec / 60);
}

export interface BuildPinOptions {
  readonly user: UserLocation;
  /** サーバー時刻（epoch秒）。鮮度は server_time 基準で計算する。 */
  readonly serverTime: number;
  /** 自分のピンかどうか（枠を強調）。 */
  readonly isSelf?: boolean;
  /** 表示言語（「N分前」の文言用・既定 ja）。 */
  readonly lang?: Lang;
}

/**
 * ピンの DOM 要素を組み立てる。CSS クラスは index.css / pinStyle.css 側で定義。
 */
export function buildPinElement(opts: BuildPinOptions): HTMLElement {
  const { user, serverTime, isSelf, lang = 'ja' } = opts;
  const ageSec = Math.max(0, serverTime - user.latest.updated_at);
  const freshness = freshnessOf(ageSec);

  const root = document.createElement('div');
  root.className = 'pin';
  // 注: opacity は MapLibre の Marker が要素の style.opacity を内部管理・上書きするため
  // ここでは設定しない。呼び出し側で marker.setOpacity() を使う（freshnessOpacityOf 参照）。
  // filter（彩度）は MapLibre が触らないので鮮度クラスで CSS 適用する。
  root.classList.add(`pin--${freshness}`);
  if (isSelf) {
    root.classList.add('pin--self');
  }

  // パルス（最新2分以内のみ・負荷対策）
  if (freshness === 'pulse') {
    const pulse = document.createElement('span');
    pulse.className = 'pin__pulse';
    root.appendChild(pulse);
  }

  // アバター（画像 or イニシャル丸）
  const avatar = document.createElement('div');
  avatar.className = 'pin__avatar';
  const initial = initialOf(user.display_name);
  if (user.avatar_url) {
    const img = document.createElement('img');
    img.className = 'pin__avatar-img';
    img.src = user.avatar_url;
    img.alt = user.display_name;
    // 読み込み失敗時はイニシャル丸にフォールバック（要件17-5 / 13-3）
    img.addEventListener('error', () => {
      img.remove();
      avatar.textContent = initial;
    });
    avatar.appendChild(img);
  } else {
    avatar.textContent = initial;
  }
  root.appendChild(avatar);

  // 古いピンには「N分前」
  if (freshness === 'stale') {
    const ago = document.createElement('span');
    ago.className = 'pin__ago';
    ago.textContent = messages(lang).time.minutesAgo(minutesAgo(ageSec));
    root.appendChild(ago);
  }

  // ステータス吹き出し（要件22-5）。アバター上部に表示、ピン移動に追従（子要素のため）。
  // 既定は1行省略。クリックで全文展開（再クリックで折りたたみ）。
  if (user.status_message) {
    const bubble = document.createElement('div');
    bubble.className = 'pin__status';
    bubble.textContent = user.status_message;
    bubble.title = user.status_message;
    bubble.addEventListener('click', (e) => {
      // ピン（クラスタ展開等）への伝播を止め、展開のトグルだけ行う
      e.stopPropagation();
      bubble.classList.toggle('pin__status--expanded');
    });
    root.appendChild(bubble);
  }

  root.title = user.status_message
    ? `${user.display_name}: ${user.status_message}`
    : user.display_name;
  return root;
}

/**
 * 経過秒（server_time - updated_at）から Marker に設定する不透明度を返す。
 * MapLibre の Marker は要素の style.opacity を上書きするため、
 * 呼び出し側でこの値を marker.setOpacity() に渡す。
 */
export function freshnessOpacityOf(ageSec: number): number {
  return opacityOf(freshnessOf(ageSec));
}
